
import React from 'react'
import { Link } from 'react-router-dom'
import {Shield, CheckCircle, Globe, ArrowRight} from 'lucide-react'

interface HeroNursingHiveProps {
  title?: string
  subtitle?: string
  imageUrl?: string
  imageAlt?: string
  overlayStrength?: 'enhanced' | 'strong'
  primaryCta?: {
    label: string
    href: string
  }
  secondaryCta?: {
    label: string
    href: string
  }
}

const HeroNursingHive: React.FC<HeroNursingHiveProps> = ({
  title = "From student nurse to verified professional — from Indo-Pak to the Philippines, from the GCC to the world.",
  subtitle = "Nursing Hive guides nursing students and working nurses from South Asia, Southeast Asia, and the GCC — from NCLEX/CGFNS preparation through verified international placement with ethical recruiters.",
  imageUrl = "https://i.imghippo.com/files/udv7454fU.png",
  imageAlt = "Two female nurses and two male nurses standing in a bright hospital corridor, symbolizing global nursing careers.",
  overlayStrength = 'enhanced',
  primaryCta = {
    label: "Register Free / Sign In (60s)",
    href: "/signup"
  },
  secondaryCta = {
    label: "Browse Resources", 
    href: "/resources"
  }
}) => {
  return (
    <>
      <style>{`
        .hero-section {
          background-image: url('${imageUrl}') !important;
          background-position: center 38% !important;
          background-size: cover !important;
          background-repeat: no-repeat !important;
          animation: fadeIn 0.8s ease-in;
          /* Desktop height */
          min-height: 72vh;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes shimmer {
          0% { background-position: -200px 0; }
          100% { background-position: 200px 0; }
        }

        .register-button {
          background: linear-gradient(90deg, #0FB5AE 25%, #3DD5CF 50%, #0FB5AE 75%);
          background-size: 200% auto;
          transition: all 0.2s ease;
          border-bottom: 3px solid #087E79;
          box-shadow: 0 4px 8px rgba(0,0,0,0.15);
        }

        .register-button:hover {
          background-color: #0FAEAA;
          transform: translateY(-2px);
          box-shadow: 0 6px 12px rgba(0,0,0,0.25);
          animation: shimmer 1s linear infinite;
        }

        .secondary-button {
          border: 2px solid #0FB5AE;
          color: #0FB5AE;
          transition: all 0.2s ease;
        }

        .secondary-button:hover {
          border-color: #0FAEAA;
          background-color: rgba(15, 181, 174, 0.1);
          transform: translateY(-2px);
        }

        /* Tablet styles */
        @media (min-width: 641px) and (max-width: 1024px) {
          .hero-section {
            background-position: center 35% !important;
            min-height: 62vh;
          }
        }

        /* Mobile styles */
        @media (max-width: 640px) {
          .hero-section {
            background-position: center 32% !important;
            background-size: cover !important;
            min-height: 55vh;
          }
          
          .hero-content {
            padding-top: 8vh !important;
          }
          
          .register-button {
            width: 90%;
            border-radius: 12px;
          }
        }

        /* Extra mobile optimization */
        @media (max-width: 480px) {
          .hero-section {
            background-position: center 32% !important;
            background-size: cover !important;
          }
          
          .hero-content {
            padding-top: 8vh !important;
          }
        }
      `}</style>

      <section className="hero-section relative w-full overflow-hidden">
        {/* Background Image with Performance Optimizations */}
        <div 
          className="absolute inset-0 bg-cover bg-no-repeat"
          style={{ 
            backgroundImage: `url(${imageUrl})`,
            backgroundPosition: 'center 38%',
            backgroundSize: 'cover'
          }}
          role="img"
          aria-label={imageAlt}
        />
        
        {/* Optimized preload for hero image */}
        <link 
          rel="preload" 
          as="image" 
          href={imageUrl}
        />
        
        {/* Enhanced Gradient Overlay for better text contrast */}
        <div className="absolute inset-0 bg-gradient-to-b from-[rgba(8,47,73,0.55)] via-[rgba(8,47,73,0.45)] to-[rgba(8,47,73,0.65)]" />
        
        {/* Mobile Top Fade */}
        <div className="absolute top-0 left-0 right-0 h-16 bg-gradient-to-b from-black/20 to-transparent md:hidden" />
        
        {/* Content Container */}
        <div className="relative z-10 h-full flex items-center">
          <div className="hero-content w-full max-w-[1180px] mx-auto px-6 md:px-8 lg:px-12 py-12 md:py-16 lg:py-[100px]">
            {/* Desktop/Tablet: Two Column Grid */}
            <div className="grid lg:grid-cols-[60%_40%] md:grid-cols-[65%_35%] gap-9 md:gap-6 lg:gap-9 items-center">
              
              {/* Left Column - Text Content */}
              <div className="text-center md:text-left space-y-6 md:space-y-8">
                {/* Eyebrow */}
                <div className="text-[#0FB5AE] text-sm font-medium uppercase tracking-wide">
                  Global nursing careers
                </div>
                
                {/* Main Headline */}
                <h1 className="text-white font-[800] text-3xl md:text-4xl lg:text-5xl xl:text-6xl leading-tight tracking-tight drop-shadow-lg">
                  {title}
                </h1>
                
                {/* Subtitle */}
                <p className="text-white/90 text-lg md:text-xl lg:text-xl leading-relaxed max-w-2xl mx-auto md:mx-0 drop-shadow-md">
                  {subtitle}
                </p>
                
                {/* CTA Buttons with 12px spacing */}
                <div className="flex flex-col sm:flex-row gap-3 justify-center md:justify-start">
                  <Link
                    to={primaryCta.href}
                    className="register-button text-white px-8 py-4 rounded-lg text-lg font-semibold inline-flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-transparent"
                  >
                    {primaryCta.label}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                  <Link
                    to={secondaryCta.href}
                    className="secondary-button px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white/10 transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-transparent sm:px-8 sm:py-4"
                  >
                    {secondaryCta.label}
                  </Link>
                </div>
                
                {/* Trust Indicators */}
                <div className="flex flex-col sm:flex-row items-center justify-center md:justify-start space-y-3 sm:space-y-0 sm:space-x-8 text-white/80 text-sm">
                  <div className="flex items-center">
                    <Shield className="h-4 w-4 mr-2 text-[#0FB5AE]" />
                    Private profiles
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="h-4 w-4 mr-2 text-[#0FB5AE]" />
                    Ethical recruitment
                  </div>
                  <div className="flex items-center">
                    <Globe className="h-4 w-4 mr-2 text-[#0FB5AE]" />
                    Multi-country options
                  </div>
                </div>
              </div>
              
              {/* Right Column - Image Space (Desktop/Tablet) */}
              <div className="hidden md:block">
                {/* This space is filled by the background image */}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default HeroNursingHive
